import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "@redux/firebase";

/**
 * Il segnale "questo utente rientra dopo N giorni".
 *
 * Il problema che risolve: `lastLoginAt` viene sovrascritto con la data di
 * adesso durante il login stesso (router/actions/actions.js, useSignInGoogle,
 * useSignInApple). Quando poi un componente lo legge, l'assenza non esiste
 * piu' — vede sempre zero giorni. Qui il valore precedente viene messo da
 * parte PRIMA che sparisca, in due posti:
 *
 *  - `sessionStorage`, per il render corrente (immediato, niente attese);
 *  - `previousLoginAt` su students/{uid}, cosi' il dato resta anche per una
 *    nuova scheda, per un refresh e per le query lato server (campagne email).
 *
 * Chi apre l'app con la sessione gia' viva non passa da nessuna di queste
 * strade: per lui `lastLoginAt` non e' stato toccato ed e' gia' la data della
 * visita precedente. Se ne occupa `absenceDaysFrom` sotto.
 */

const SESSION_KEY = "returnVisitAbsenceDays";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Timestamp Firestore, Date o stringa -> Date. null se non interpretabile. */
export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") {
    try {
      return value.toDate();
    } catch (_) {
      return null;
    }
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/** Giorni interi fra `value` e adesso. null se la data non c'e'. */
const daysSince = (value, now) => {
  const date = toDate(value);
  if (!date) return null;
  return Math.floor((now.getTime() - date.getTime()) / MS_PER_DAY);
};

/**
 * Chiamata dal login, con i dati letti PRIMA della scrittura di lastLoginAt.
 * Non attende la scrittura su Firestore: il login non deve rallentare per
 * un campo di misura, e se fallisce resta comunque il valore in sessione.
 *
 * @param {string} uid
 * @param {*} previousLastLoginAt il lastLoginAt letto dal documento
 * @returns {number|null} i giorni di assenza, per chi volesse usarli subito
 */
export const markReturnVisit = (uid, previousLastLoginAt, now = new Date()) => {
  const days = daysSince(previousLastLoginAt, now);

  try {
    if (days !== null) sessionStorage.setItem(SESSION_KEY, String(days));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch (_) {
    /* Safari in navigazione privata: pazienza, resta il campo su Firestore. */
  }

  const previous = toDate(previousLastLoginAt);
  if (uid && previous) {
    // `Promise.resolve` perche' la scrittura non deve mai poter far esplodere
    // il login: ne' con un rifiuto, ne' se updateDoc torna qualcosa che non e'
    // una promise (succede con i doppi di test e con le build offline).
    try {
      Promise.resolve(updateDoc(doc(firestore, "students", uid), { previousLoginAt: previous }))
        .catch(() => { /* campo di misura: si perde e pazienza */ });
    } catch (_) {
      /* idem */
    }
  }

  return days;
};

/**
 * I giorni di assenza dell'utente che sta guardando l'app, in ordine di
 * affidabilita':
 *
 *  1. quelli messi da parte dal login di questa sessione;
 *  2. `previousLoginAt`, se `lastLoginAt` e' di oggi (cioe': si e' appena
 *     loggato, e il valore buono e' quello precedente);
 *  3. `lastLoginAt`, per chi e' entrato con la sessione gia' viva e quindi
 *     non l'ha aggiornato.
 *
 * @returns {number|null} null quando non c'e' modo di saperlo (utente nuovo)
 */
export const absenceDaysFrom = (user, now = new Date()) => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored !== null && stored !== "") {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) return parsed;
    }
  } catch (_) {
    /* niente sessionStorage: si prosegue con i campi del documento */
  }

  if (!user) return null;

  const fromLast = daysSince(user.lastLoginAt, now);
  if (fromLast === null) return null;

  if (fromLast < 1) {
    const fromPrevious = daysSince(user.previousLoginAt, now);
    return fromPrevious === null ? fromLast : fromPrevious;
  }

  return fromLast;
};

/** Solo per i test: azzera il valore messo da parte. */
export const clearReturnVisit = () => {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (_) {
    /* no-op */
  }
};
